import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import { useConfirm } from "../components/ConfirmDialog";

const IOS_KEY = "min_app_version_ios";
const ANDROID_KEY = "min_app_version_android";
const DISABLED_VALUE = "0.0.0";
const VERSION_RE = /^\d+\.\d+\.\d+$/;

type Platform = "ios" | "android";

function isValid(v: string): boolean {
  return VERSION_RE.test(v.trim());
}

/// Keep only digits + at most two dots (three numeric segments), cap each
/// segment at 4 digits. Prevents typing letters, symbols, or nonsense like
/// "...." or "99999999999".
function sanitize(raw: string): string {
  const digitsAndDots = raw.replace(/[^\d.]/g, "");
  const parts = digitsAndDots.split(".").slice(0, 3);
  return parts.map((p) => p.slice(0, 4)).join(".");
}

function cmp(a: string, b: string): number {
  const pa = a.split(".").map((s) => parseInt(s, 10) || 0);
  const pb = b.split(".").map((s) => parseInt(s, 10) || 0);
  for (let i = 0; i < 3; i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

interface FieldState {
  current: string;
  saved: string;
  touched: boolean;
}

export default function MinAppVersionPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const [ios, setIos] = useState<FieldState>({
    current: DISABLED_VALUE,
    saved: DISABLED_VALUE,
    touched: false,
  });
  const [android, setAndroid] = useState<FieldState>({
    current: DISABLED_VALUE,
    saved: DISABLED_VALUE,
    touched: false,
  });

  const flashTimer = useRef<number | null>(null);
  const { confirm, dialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      const settings = await api.listSettings();
      const map = new Map(settings.map((s) => [s.key, s.value]));
      const iosVal = map.get(IOS_KEY) || DISABLED_VALUE;
      const androidVal = map.get(ANDROID_KEY) || DISABLED_VALUE;
      setIos({ current: iosVal, saved: iosVal, touched: false });
      setAndroid({ current: androidVal, saved: androidVal, touched: false });
    } catch (err) {
      console.error("Failed to load min app versions", err);
      setError("Не удалось загрузить настройки");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    };
  }, []);

  const dirty = ios.current !== ios.saved || android.current !== android.saved;
  const iosValid = isValid(ios.current);
  const androidValid = isValid(android.current);
  const allValid = iosValid && androidValid;
  const canSave = dirty && allValid && !saving;

  const handleSave = useCallback(async () => {
    const iosTrim = ios.current.trim();
    const androidTrim = android.current.trim();
    if (!isValid(iosTrim) || !isValid(androidTrim)) return;

    // Confirm if bumping higher (potentially mass-blocking users)
    const iosBump = cmp(iosTrim, ios.saved) > 0 && iosTrim !== DISABLED_VALUE;
    const androidBump =
      cmp(androidTrim, android.saved) > 0 && androidTrim !== DISABLED_VALUE;
    if (iosBump || androidBump) {
      const changes: string[] = [];
      if (iosBump) changes.push(`iOS: ${ios.saved} → ${iosTrim}`);
      if (androidBump) changes.push(`Android: ${android.saved} → ${androidTrim}`);
      const confirmed = await confirm({
        title: "Повысить минимальную версию?",
        body: (
          <>
            <p>
              Всем, у кого версия приложения ниже, придётся обновить его, прежде
              чем пользоваться дальше.
            </p>
            {changes.map((change) => (
              <p key={change}>{change}</p>
            ))}
          </>
        ),
        confirmLabel: "Повысить и сохранить",
        danger: true,
      });
      if (!confirmed) return;
    }

    setSaving(true);
    setError(null);
    setSavedFlash(false);
    try {
      await api.batchUpsertSettings([
        { key: IOS_KEY, value: iosTrim },
        { key: ANDROID_KEY, value: androidTrim },
      ]);
      setIos({ current: iosTrim, saved: iosTrim, touched: false });
      setAndroid({ current: androidTrim, saved: androidTrim, touched: false });
      setSavedFlash(true);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setSavedFlash(false), 2500);
    } catch (err: any) {
      console.error("Failed to save min app versions", err);
      setError(err?.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  }, [ios, android, confirm]);

  const handleReset = () => {
    setIos((s) => ({ ...s, current: s.saved, touched: false }));
    setAndroid((s) => ({ ...s, current: s.saved, touched: false }));
    setError(null);
  };

  const handleDisableBlock = () => {
    setIos((s) => ({ ...s, current: DISABLED_VALUE, touched: true }));
    setAndroid((s) => ({ ...s, current: DISABLED_VALUE, touched: true }));
    setError(null);
  };

  // Cmd+S / Ctrl+S to save
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        if (canSave) handleSave();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [canSave, handleSave]);

  const cards = useMemo(
    () => [
      {
        platform: "ios" as Platform,
        title: "iOS",
        subtitle: "Минимальная версия для сборок из App Store",
        key: IOS_KEY,
        state: ios,
        valid: iosValid,
        setState: setIos,
      },
      {
        platform: "android" as Platform,
        title: "Android",
        subtitle: "Минимальная версия для сборок из Google Play",
        key: ANDROID_KEY,
        state: android,
        valid: androidValid,
        setState: setAndroid,
      },
    ],
    [ios, android, iosValid, androidValid]
  );

  return (
    <div className="page mav-page">
      <div className="mav-header">
        <div>
          <h2>Минимальная версия приложения</h2>
          <p className="page-description">
            Минимальная версия приложения для каждой платформы. Пользователи с
            версией ниже увидят при запуске полноэкранный экран «Обновите
            приложение». Чтобы отключить блокировку, укажите{" "}
            <code>{DISABLED_VALUE}</code> для обеих платформ.
          </p>
        </div>
        <button
          className="mav-link-btn"
          onClick={handleDisableBlock}
          disabled={
            saving ||
            (ios.current === DISABLED_VALUE && android.current === DISABLED_VALUE)
          }
          title="Выставить 0.0.0 для обеих платформ — никто не блокируется"
        >
          Отключить блокировку
        </button>
      </div>

      <div className="card-list">
        {loading
          ? [0, 1].map((i) => <SkeletonCard key={i} />)
          : cards.map((c) => (
              <VersionCard
                key={c.platform}
                title={c.title}
                subtitle={c.subtitle}
                settingKey={c.key}
                state={c.state}
                valid={c.valid}
                disabled={saving}
                onChange={(v) =>
                  c.setState((s) => ({
                    ...s,
                    current: sanitize(v),
                    touched: true,
                  }))
                }
                onBlur={() => c.setState((s) => ({ ...s, touched: true }))}
                onEnter={() => {
                  if (canSave) handleSave();
                }}
              />
            ))}
      </div>

      <div className="mav-actions">
        <button
          className="mav-btn-primary"
          onClick={handleSave}
          disabled={!canSave}
          title="⌘S"
        >
          {saving ? "Сохраняем…" : "Сохранить"}
        </button>
        <button
          className="mav-btn-secondary"
          onClick={handleReset}
          disabled={!dirty || saving}
        >
          Сбросить
        </button>

        <div className="mav-status" aria-live="polite">
          {error ? (
            <span className="mav-status-error">{error}</span>
          ) : savedFlash ? (
            <span className="mav-status-ok">Сохранено ✓</span>
          ) : dirty ? (
            <span className="mav-status-dirty">Есть несохранённые изменения</span>
          ) : (
            <span className="mav-status-muted">Всё сохранено</span>
          )}
        </div>
      </div>

      {dialog}
    </div>
  );
}

interface VersionCardProps {
  title: string;
  subtitle: string;
  settingKey: string;
  state: FieldState;
  valid: boolean;
  disabled: boolean;
  onChange: (v: string) => void;
  onBlur: () => void;
  onEnter: () => void;
}

function VersionCard({
  title,
  subtitle,
  settingKey,
  state,
  valid,
  disabled,
  onChange,
  onBlur,
  onEnter,
}: VersionCardProps) {
  const isDisabled = state.saved === DISABLED_VALUE;
  const isDirty = state.current !== state.saved;
  const showError = state.touched && !valid;

  return (
    <div className={`mav-card ${isDirty ? "is-dirty" : ""}`}>
      <div className="mav-card-info">
        <div className="mav-card-title-row">
          <h3>{title}</h3>
          {isDisabled ? (
            <span className="mav-badge mav-badge-muted">Блокировка выключена</span>
          ) : (
            <span className="mav-badge mav-badge-active">Действует: {state.saved}</span>
          )}
          {isDirty && <span className="mav-badge mav-badge-dirty">Изменено</span>}
        </div>
        <p>{subtitle}</p>
        <code>{settingKey}</code>
      </div>
      <div className="mav-card-input">
        <label className="mav-input-label" htmlFor={`min-version-${settingKey}`}>
          Минимальная версия
        </label>
        <input
          id={`min-version-${settingKey}`}
          className={`mav-input ${showError ? "has-error" : ""}`}
          value={state.current}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter") onEnter();
          }}
          placeholder="2.0.0"
          spellCheck={false}
          autoComplete="off"
          disabled={disabled}
          inputMode="decimal"
        />
        <span className={`mav-hint ${showError ? "is-error" : ""}`}>
          {showError ? "Формат: мажор.минор.патч, например 2.0.0" : "например, 2.0.0"}
        </span>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="mav-card mav-card-skeleton">
      <div className="mav-card-info">
        <div className="skeleton-line skeleton-line-title" />
        <div className="skeleton-line skeleton-line-sub" />
        <div className="skeleton-line skeleton-line-code" />
      </div>
      <div className="mav-card-input">
        <div className="skeleton-line skeleton-line-label" />
        <div className="skeleton-line skeleton-line-input" />
      </div>
    </div>
  );
}
